/** Таблицы «Аналитики ОПТ»: по месяцам (три строки на месяц — отгружено / к отгрузке /
 *  просрочено, никогда не сложены) и список товаров (в карточках «без цены» / «без ставки склада»).
 *  Стиль — как в «Аналитике МП»: text-xs, липкая шапка, деньги компактно (тыс/млн, точное — в
 *  подсказке). Покупатели и товары периода — дерево (`TreeTable.tsx`). Клик по строке — карточка. */
import React from "react";

import type { Agg, Kind, MonthRow, PerKind, ProductRow } from "@/api/wholesale";
import { cn } from "@/lib/utils";
import { monthLabel, rub } from "../ozon-analytics/format";
import { tdNum, thClass } from "../ozon-analytics/ui";
import {
  compactCell,
  costCell,
  KIND_LABEL,
  marginCell,
  netRevenueCell,
  productTitle,
  qty,
  revenueCell,
  type Card,
} from "./calc";
import { MoneyTd } from "./parts";

/** Шапка таблиц раздела — липкая и непрозрачная (строки под ней не просвечивают). */
export const STICKY_HEAD = "sticky top-0 z-10 bg-muted text-[11px] shadow-[inset_0_-1px_0_hsl(var(--border))]";
/** Контейнер таблицы со своей вертикальной прокруткой (высота — экран минус шапка страницы):
 *  без неё `sticky top-0` не к чему прилипать — контейнер с overflow-x не прокручивается. */
export const TABLE_BOX = "w-fit max-w-full max-h-[calc(100vh-8rem)] overflow-auto rounded-md border bg-card";
/** То же во всю ширину страницы (дерево). */
export const TABLE_BOX_FULL = "w-full max-h-[calc(100vh-8rem)] overflow-auto rounded-md border bg-card";

const KIND_TONE: Record<Kind, string> = {
  shipped: "",
  planned: "text-sky-700 dark:text-sky-400",
  overdue: "text-red-700 dark:text-red-400",
};

const neg = (v: number | null | undefined): number | null => (v === null || v === undefined ? null : -v);

function AggCells({ a }: { a: Agg | null }): React.ReactElement {
  const m = marginCell(a);
  return (
    <>
      <td className={tdNum}>{a ? qty(a.quantity) : "—"}</td>
      <MoneyTd cell={compactCell(revenueCell(a), a?.revenue_gross)} testId="cell-revenue" />
      <MoneyTd cell={compactCell(netRevenueCell(a), a?.revenue_net)} testId="cell-net" />
      <MoneyTd
        cell={compactCell(costCell(a?.cogs_goods ?? null, a?.cogs_goods_missing ?? EMPTY, a, "goods"), neg(a?.cogs_goods))}
        testId="cell-goods"
      />
      <MoneyTd
        cell={compactCell(costCell(a?.cogs_wh ?? null, a?.cogs_wh_missing ?? EMPTY, a, "wh"), neg(a?.cogs_wh))}
        testId="cell-wh"
      />
      <MoneyTd cell={compactCell(m, a?.net_margin)} testId="cell-margin" />
      <td className={tdNum} data-testid="cell-margin-pct">{m.pct}</td>
    </>
  );
}

const EMPTY = { rows: 0, quantity: 0, revenue_gross: 0, revenue_unknown_rows: 0 };

function AggHead({ first }: { first: string }): React.ReactElement {
  return (
    <tr>
      <th className={cn(thClass, "text-left")}>{first}</th>
      <th className={cn(thClass, "text-left")}>Вид</th>
      <th className={thClass}>Шт</th>
      <th className={thClass} title="выручка с НДС">Выручка с НДС</th>
      <th className={thClass}>Без НДС</th>
      <th className={thClass} title="себестоимость производства, с НДС">Себестоимость</th>
      <th className={thClass} title="склад производства, без НДС">Склад</th>
      <th className={thClass} title="выручка с НДС − закупка − склад (как маржа МП)">Маржа</th>
      <th className={thClass}>Маржа %</th>
    </tr>
  );
}

function Flag({ a }: { a: Agg | null }): React.ReactElement | null {
  if (!a) return null;
  // покупатель без ИНН не помечается (решение владельца 22.10.2026: иностранные — норма)
  const marks: string[] = [];
  if (a.base_unresolved.rows > 0) marks.push("корр. без накладной");
  if (marks.length === 0) return null;
  return (
    <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800 dark:bg-amber-950 dark:text-amber-300" data-testid="row-flag">
      {marks.join(", ")}
    </span>
  );
}

export function MonthsTable({
  months,
  totals,
  onOpen,
}: {
  months: MonthRow[];
  totals: PerKind;
  onOpen: (c: Card) => void;
}): React.ReactElement {
  return (
    <div className={TABLE_BOX}>
      <table className="text-xs" data-testid="months-table">
        <thead className={STICKY_HEAD}>
          <AggHead first="Месяц" />
        </thead>
        <tbody>
          {months.map((m) =>
            (["shipped", "planned", "overdue"] as const).map((k, i) => (
              <tr
                key={`${m.month}:${k}`}
                className={cn("hover:bg-accent/50", i === 0 && "border-t", m[k] && "cursor-pointer")}
                onClick={m[k] ? () => onOpen({ type: "month", month: m.month, kind: k }) : undefined}
                data-testid="month-row"
                data-month={m.month}
                data-kind={k}
              >
                <td className="px-2 py-1 whitespace-nowrap">{i === 0 ? monthLabel(m.month) : ""}</td>
                <td className={cn("px-2 py-1 whitespace-nowrap", KIND_TONE[k])}>
                  {KIND_LABEL[k]}
                  <Flag a={m[k]} />
                </td>
                <AggCells a={m[k]} />
              </tr>
            )),
          )}
        </tbody>
        <tfoot>
          {(["shipped", "planned", "overdue"] as const).map((k, i) => (
            <tr key={k} className={cn("bg-muted/40 font-medium", i === 0 && "border-t-2")} data-testid="total-row" data-kind={k}>
              <td className="px-2 py-1">{i === 0 ? "Итого" : ""}</td>
              <td className={cn("px-2 py-1", KIND_TONE[k])}>{KIND_LABEL[k]}</td>
              <AggCells a={totals[k]} />
            </tr>
          ))}
        </tfoot>
      </table>
    </div>
  );
}

/** Короткие колонки вида для разрезов: шт, выручка, маржа. */
function KindCols({ a, margin = true }: { a: Agg | null; margin?: boolean }): React.ReactElement {
  return (
    <>
      <td className={tdNum}>{a ? qty(a.quantity) : "—"}</td>
      <MoneyTd cell={compactCell(revenueCell(a), a?.revenue_gross)} />
      {margin && <MoneyTd cell={compactCell(marginCell(a), a?.net_margin)} testId="cell-margin" />}
    </>
  );
}

function SliceHead({ first, extra }: { first: string; extra?: React.ReactNode }): React.ReactElement {
  return (
    <>
      <tr>
        <th className={cn(thClass, "text-left")} rowSpan={2}>
          {first}
        </th>
        {extra}
        <th className={cn(thClass, "text-center")} colSpan={3}>
          Отгружено
        </th>
        <th className={cn(thClass, "text-center", KIND_TONE.planned)} colSpan={2}>
          К отгрузке
        </th>
        <th className={cn(thClass, "text-center", KIND_TONE.overdue)} colSpan={2}>
          Просрочено
        </th>
      </tr>
      <tr>
        <th className={thClass}>Шт</th>
        <th className={thClass}>Выручка</th>
        <th className={thClass}>Маржа</th>
        <th className={thClass}>Шт</th>
        <th className={thClass}>Выручка</th>
        <th className={thClass}>Шт</th>
        <th className={thClass}>Выручка</th>
      </tr>
    </>
  );
}

function priceText(p: ProductRow): string {
  if (p.price_min === null || p.price_max === null) return "нет цены";
  return p.price_min === p.price_max ? rub(p.price_min) : `${rub(p.price_min)} … ${rub(p.price_max)}`;
}

/** Строк товара с данным проблемным статусом цены (по всем видам). */
function statusRows(p: PerKind, status: "no_price" | "no_warehouse_rate"): number {
  return (["shipped", "planned", "overdue"] as const).reduce((s, k) => {
    const a = p[k];
    return s + (a ? a.price_status[status].rows : 0);
  }, 0);
}

export function ProductsTable({
  rows,
  onOpen,
  testId = "products-table",
}: {
  rows: ProductRow[];
  onOpen: (c: Card) => void;
  testId?: string;
}): React.ReactElement {
  if (rows.length === 0) {
    return <p className="rounded-md border px-4 py-6 text-sm text-muted-foreground" data-testid="products-empty">Товаров в периоде нет.</p>;
  }
  return (
    <div className={TABLE_BOX}>
      <table className="text-xs" data-testid={testId}>
        <thead className={STICKY_HEAD}>
          <SliceHead
            first="Товар (номенклатура × характеристика)"
            extra={
              <th className={thClass} rowSpan={2} title="себестоимость за штуку с НДС (мин … макс по строкам)">
                Закуп. цена
              </th>
            }
          />
        </thead>
        <tbody>
          {rows.map((p) => {
            const np = statusRows(p, "no_price");
            const nr = statusRows(p, "no_warehouse_rate");
            return (
              <tr
                key={`${p.nomenclature_ref}:${p.characteristic_ref ?? ""}`}
                className="cursor-pointer border-t hover:bg-accent/50"
                onClick={() =>
                  onOpen({ type: "product", nomenclature: p.nomenclature_ref, characteristic: p.characteristic_ref ?? "" })
                }
                data-testid="product-row"
              >
                <td className="px-2 py-1">
                  <div className="font-medium">{productTitle(p)}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.code_1c ?? "—"} · {p.characteristic_name ?? "без характеристики"}
                    {np > 0 && (
                      <span className="ml-1 rounded bg-amber-100 px-1 text-amber-800 dark:bg-amber-950 dark:text-amber-300" data-testid="no-price">
                        нет цены: {np} стр.
                      </span>
                    )}
                    {nr > 0 && (
                      <span className="ml-1 rounded bg-amber-100 px-1 text-amber-800 dark:bg-amber-950 dark:text-amber-300" data-testid="no-rate">
                        нет ставки склада: {nr} стр.
                      </span>
                    )}
                  </div>
                </td>
                <td className={cn(tdNum, p.price_min === null && "text-amber-700 dark:text-amber-400")}>{priceText(p)}</td>
                <KindCols a={p.shipped} />
                <KindCols a={p.planned} margin={false} />
                <KindCols a={p.overdue} margin={false} />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
